import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import { service } from '@ember/service';
import { guidFor } from '@ember/object/internals';
import t from 'ember-intl/helpers/t';
import { on } from '@ember/modifier';
import { eq, not, and } from 'ember-truth-helpers';
import isEmpty from 'ember-truth-helpers/helpers/is-empty';
import sortBy from 'ilios-common/helpers/sort-by';
import CopyButton from 'ilios-common/components/copy-button';
import perform from 'ember-concurrency/helpers/perform';
import mouseHoverToggle from 'ilios-common/modifiers/mouse-hover-toggle';
import set from 'ember-set-helper/helpers/set';
import LoadingSpinner from 'ilios-common/components/loading-spinner';
import IliosTooltip from 'ilios-common/components/ilios-tooltip';
import DownloadDropdown from 'frontend/components/download-dropdown';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
import { faCheck, faCopy, faDownload, faPlay } from '@fortawesome/free-solid-svg-icons';

export default class ReportsCurriculumHeaderComponent extends Component {
  @service flashMessages;
  @service intl;
  @service store;

  get copyButtonId() {
    return `curriculum-report-copy-button-${guidFor(this)}`;
  }

  get copyButtonElement() {
    return document.getElementById(this.copyButtonId);
  }

  get runButtonId() {
    return `curriculum-report-run-button-${guidFor(this)}`;
  }

  get runButtonElement() {
    return document.getElementById(this.runButtonId);
  }

  textCopied = task({ restartable: true }, async () => {
    this.flashMessages.success(this.intl.t('general.copiedCurriculumReportUrl'));
  });

  popperOptions = {
    placement: 'right',
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom'],
        },
      },
    ],
  };

  get countSelectedSchools() {
    return this.args.selectedSchoolIds ? this.args.selectedSchoolIds.length : 0;
  }

  get hasMultipleSchools() {
    return this.countSelectedSchools > 1;
  }

  get reportList() {
    if (this.hasMultipleSchools) {
      return [
        {
          value: 'courseCompetencies',
          label: this.intl.t('general.courseCompetencies'),
          summary: this.intl.t('general.courseCompetenciesReportSummaryMultiSchool', {
            courseCount: this.args.countSelectedCourses,
            schoolCount: this.countSelectedSchools,
          }),
        },
        {
          value: 'instructionalTime',
          label: this.intl.t('general.instructionalTime'),
          summary: this.intl.t('general.instructionalTimeReportSummaryMultiSchool', {
            courseCount: this.args.countSelectedCourses,
            schoolCount: this.countSelectedSchools,
          }),
        },
        {
          value: 'learnerGroups',
          label: this.intl.t('general.learnerGroups'),
          summary: this.intl.t('general.learnerGroupsReportSummaryMultiSchool', {
            courseCount: this.args.countSelectedCourses,
            schoolCount: this.countSelectedSchools,
          }),
        },
        {
          value: 'sessionObjectives',
          label: this.intl.t('general.sessionObjectives'),
          summary: this.intl.t('general.sessionObjectivesReportSummaryMultiSchool', {
            courseCount: this.args.countSelectedCourses,
            schoolCount: this.countSelectedSchools,
          }),
        },
        {
          value: 'sessionOfferings',
          label: this.intl.t('general.sessionOfferings'),
          summary: this.intl.t('general.sessionOfferingsReportSummaryMultiSchool', {
            courseCount: this.args.countSelectedCourses,
            schoolCount: this.countSelectedSchools,
          }),
        },
        {
          value: 'taggedTerms',
          label: this.intl.t('general.taggedTerms'),
          summary: this.intl.t('general.taggedTermsReportSummaryMultiSchool', {
            courseCount: this.args.countSelectedCourses,
            schoolCount: this.countSelectedSchools,
          }),
        },
      ];
    } else {
      return [
        {
          value: 'courseCompetencies',
          label: this.intl.t('general.courseCompetencies'),
          summary: this.intl.t('general.courseCompetenciesReportSummary', {
            courseCount: this.args.countSelectedCourses,
          }),
        },
        {
          value: 'instructionalTime',
          label: this.intl.t('general.instructionalTime'),
          summary: this.intl.t('general.instructionalTimeReportSummary', {
            courseCount: this.args.countSelectedCourses,
          }),
        },
        {
          value: 'learnerGroups',
          label: this.intl.t('general.learnerGroups'),
          summary: this.intl.t('general.learnerGroupsReportSummary', {
            courseCount: this.args.countSelectedCourses,
          }),
        },
        {
          value: 'sessionObjectives',
          label: this.intl.t('general.sessionObjectives'),
          summary: this.intl.t('general.sessionObjectivesReportSummary', {
            courseCount: this.args.countSelectedCourses,
          }),
        },
        {
          value: 'sessionOfferings',
          label: this.intl.t('general.sessionOfferings'),
          summary: this.intl.t('general.sessionOfferingsReportSummary', {
            courseCount: this.args.countSelectedCourses,
          }),
        },
        {
          value: 'taggedTerms',
          label: this.intl.t('general.taggedTerms'),
          summary: this.intl.t('general.taggedTermsReportSummary', {
            courseCount: this.args.countSelectedCourses,
          }),
        },
      ];
    }
  }

  get selectedReport() {
    return this.reportList.find((r) => r.value === this.args.selectedReportValue);
  }

  changeSelectedReport = ({ target }) => {
    this.args.changeSelectedReport(target.value);
  };
  getReportUrl = () => {
    return window.location.href;
  };
  <template>
    <div class="reports-curriculum-header" data-test-reports-curriculum-header>
      <div class="input-buttons">
        {{#if (and @countSelectedCourses this.selectedReport)}}
          <CopyButton
            @getClipboardText={{this.getReportUrl}}
            @success={{perform this.textCopied}}
            aria-label={{t "general.copyCurriculumReportUrlTooltip"}}
            id={{this.copyButtonId}}
            {{mouseHoverToggle (set this "showCopyTooltip")}}
          >
            <FaIcon @icon={{faCopy}} />
            {{t "general.copyCurriculumReportUrl"}}
          </CopyButton>
          {{#if this.showCopyTooltip}}
            <IliosTooltip
              @target={{this.copyButtonElement}}
              @options={{this.popperOptions}}
              class="ics-feed-tooltip"
            >
              {{t "general.copyCurriculumReportUrlTooltip"}}
            </IliosTooltip>
          {{/if}}
        {{/if}}
        {{#if @showReportResults}}
          {{#if @loading}}
            <div class="loading-results">
              <LoadingSpinner />
            </div>
          {{else}}
            {{#if (eq @selectedReportValue "taggedTerms")}}
              <DownloadDropdown @items={{@options}} @action={{@download}} />
            {{else}}
              <button type="button" {{on "click" @download}} data-test-download>
                {{#if @finished}}
                  <FaIcon @icon={{faCheck}} />
                {{else}}
                  <FaIcon @icon={{faDownload}} />
                {{/if}}
                {{t "general.downloadResults"}}
              </button>
            {{/if}}
          {{/if}}
          <button type="button" class="cancel text" {{on "click" @close}} data-test-close>
            {{t "general.close"}}
          </button>
        {{else}}
          <button
            type="button"
            class="done text"
            {{on "click" @runReport}}
            disabled={{not (and @countSelectedCourses this.selectedReport)}}
            id={{this.runButtonId}}
            {{mouseHoverToggle (set this "showRunTooltip")}}
            data-test-run
          >
            <FaIcon @icon={{faPlay}} @title={{t "general.runReport"}} />
            {{t "general.runReport"}}
          </button>
          {{#if this.showRunTooltip}}
            <IliosTooltip
              @target={{this.runButtonElement}}
              @options={{this.popperOptions}}
              class="ics-feed-tooltip"
            >
              {{t "general.runReport"}}
            </IliosTooltip>
          {{/if}}
        {{/if}}
      </div>
      <div class="run">
        <p>
          {{#unless @showReportResults}}
            <label data-test-report-selector>
              <span data-test-report-selector-label>
                {{t "general.selectCurriculumReportType"}}:
              </span>
              <select {{on "change" this.changeSelectedReport}}>
                <option selected={{isEmpty this.selectedReport}} value>
                  {{t "general.selectPolite"}}
                </option>
                {{#each (sortBy "label" this.reportList) as |report|}}
                  <option
                    value={{report.value}}
                    selected={{eq report.value this.selectedReport.value}}
                  >
                    {{report.label}}
                  </option>
                {{/each}}
              </select>
            </label>
          {{/unless}}
          <div data-test-run-summary>
            {{#if this.selectedReport}}
              {{#if @countSelectedCourses}}
                <div data-test-selected-report-label-summary>
                  {{t "general.run"}}
                  {{this.selectedReport.label}}
                  {{this.selectedReport.summary}}
                </div>
              {{else}}
                <div data-test-selected-report-label-summary>
                  {{t "general.selectCoursesToRunReport"}}
                </div>
              {{/if}}
            {{/if}}
          </div>
        </p>
      </div>
    </div>
  </template>
}
